import { CommonModule } from '@angular/common'
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core'
import { MatIconModule } from '@angular/material/icon'
import { MatTableDataSource, MatTableModule } from '@angular/material/table'
import { MatButtonModule } from '@angular/material/button'
import { MatCardModule } from '@angular/material/card'
import { Client, Voucher } from '../../control-panel.service'
import { NgxMaskPipe } from 'ngx-mask'
import { MatDividerModule } from '@angular/material/divider'
import { Category } from '@monorepo-channels/channels/domain'
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout'
import { BehaviorSubject, Subject, takeUntil } from 'rxjs'
import { ConciergeAddBenefitComponent } from './add-benefit/add-benefit.component'
import { MatDialog } from '@angular/material/dialog'
import { ConciergeCancelVoucherComponent } from './cancel-voucher/cancel-voucher.component'

@Component({
	standalone: true,
	selector: 'feature-pm-benefits-celebrations-tab-content',
	templateUrl: './tab-benefits-celebrations.component.html',
	styleUrls: ['./tab-benefits-celebrations.component.scss'],
	imports: [
		MatTableModule,
		CommonModule,
		MatIconModule,
		MatButtonModule,
		MatCardModule,
		MatDividerModule,
		NgxMaskPipe,
	],
})
export class TabBenefitsCelebrationsComponent implements OnInit, OnDestroy {
	@Input() isLoadingLogin!: boolean
	@Input() dataSource!: MatTableDataSource<Client>
	@Input() clients!: Client[]
	@Input() displayedColumns!: string[]
	@Output() clientUpdated = new EventEmitter<void>()

	private breakpointObserver = inject(BreakpointObserver)
	private dialog = inject(MatDialog)

	currentIndex: number = 0
	itemsPerPage$ = new BehaviorSubject<number>(3)
	private destroy$ = new Subject<void>()
	selectedClientId: string | null = null

	ngOnInit(): void {
		this.breakpointObserver
			.observe([Breakpoints.Handset])
			.pipe(takeUntil(this.destroy$))
			.subscribe(result => {
				this.itemsPerPage$.next(result.matches ? 1 : 3) // Define 1 item por vez no mobile, 3 no desktop
			})
	}

	ngOnDestroy(): void {
		this.destroy$.next()
		this.destroy$.complete()
	}

	get selectedClient(): Client | undefined {
		return this.clients.find(c => c._id === this.selectedClientId)
	}

	selectClient(clientId: string): void {
		this.selectedClientId = clientId
	}

	// Retorna os vouchers visíveis de acordo com o índice atual
	get visibleVouchers(): Voucher[] {
		if (!this.selectedClient) return []
		const { vouchers = [] } = this.selectedClient
		const start = this.currentIndex
		const end = this.currentIndex + this.itemsPerPage$.value
		return vouchers.slice(start, end)
	}

	handlePrevious(): void {
		if (this.currentIndex > 0) this.currentIndex--
	}

	handleNext(): void {
		if (
			this.selectedClient?.vouchers &&
			this.currentIndex < this.selectedClient.vouchers.length - this.itemsPerPage$.value
		) {
			this.currentIndex++
		}
	}

	isFullRegistration(client: Client): string {
		return client.name && client.phone && client.email && client.password && client.categories.length > 0
			? 'Completo'
			: 'Incompleto'
	}

	isCardComplete(category: Category): string {
		return category.gatewayId !== '' ? 'Completo' : 'Incompleto'
	}

	getClientName(clientId: string): string {
		const client = this.dataSource.data.find(c => c._id === clientId)
		return client ? `${client.name} ${client.lastName}` : 'Cliente não encontrado'
	}

	getStatusName(status: string): string {
		switch (status) {
			case 'active':
				return 'Ativo'
			case 'expired':
				return 'Expirado'
			case 'canceled':
				return 'Cancelado'
			case 'used':
				return 'Utilizado'
			case 'validated':
				return 'Validado'
			default:
				return 'Desconhecido'
		}
	}

	addBenefit(client: Client) {
		const dialogRef = this.dialog.open(ConciergeAddBenefitComponent, {
			width: '500px',
			maxHeight: '90vh',
			data: client,
			disableClose: true,
		})

		dialogRef.afterClosed().subscribe(result => {
			if (result === 'updated') {
				const updatedClient = this.clients.find(c => c._id === client?._id)
				if (updatedClient) {
					this.clientUpdated.emit()
				}
			}
		})
	}

	cancelVoucher(voucher: Voucher) {
		const formattedVoucher = {
			...voucher,
			clientName: this.getClientName(voucher.clientId),
		}
		const dialogRef = this.dialog.open(ConciergeCancelVoucherComponent, {
			width: '400px',
			data: formattedVoucher,
			disableClose: true,
		})
		dialogRef.afterClosed().subscribe(result => {
			if (result === 'updated') {
				const client = this.clients.find(c => c._id === voucher.clientId)
				if (client) {
					this.clientUpdated.emit()
				}
			}
		})
	}
}
